import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack
} from '@mui/material';
import { Visibility, Info, Warning, Error, ErrorOutline } from '@mui/icons-material';
import { systemLogService, SystemLog } from '../../services/systemLogService';

const SystemLogs: React.FC = () => {
  const [logs, setLogs] = useState<SystemLog[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [viewingLog, setViewingLog] = useState<SystemLog | null>(null);

  useEffect(() => {
    loadLogs();
  }, [filterSeverity, filterEntity]);

  const loadLogs = async () => {
    try {
      const filters: any = {};
      
      if (filterSeverity !== 'all') {
        filters.severity = { $eq: filterSeverity };
      }
      
      if (filterEntity !== 'all') {
        filters.entity = { $eq: filterEntity };
      }

      const response = await systemLogService.getAll({
        filters,
        sort: 'createdAt:desc',
        'pagination[pageSize]': 200
      });
      setLogs(response.data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const handleViewDetails = (log: any) => {
    setViewingLog(log);
    setDetailsOpen(true);
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    setViewingLog(null);
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'info': return <Info color="info" />;
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Error color="error" />;
      case 'critical': return <ErrorOutline color="error" />;
      default: return <Info />;
    }
  };

  const getSeverityColor = (severity: string): any => {
    switch (severity) {
      case 'info': return 'info';
      case 'warning': return 'warning';
      case 'error': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const filteredLogs = logs.filter(log => {
    const attrs = log.attributes || log;
    if (!searchTerm) return true;
    
    return (
      attrs.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attrs.entity?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attrs.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      attrs.user?.data?.attributes?.username?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const entitySet = new Set(logs.map(log => (log.attributes || log).entity));
  const entities = Array.from(entitySet).filter(Boolean);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">System Activity Logs</Typography>
        <Chip label={`${filteredLogs.length} Logs`} color="primary" />
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            placeholder="Search logs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={filterSeverity}
              label="Severity"
              onChange={(e) => setFilterSeverity(e.target.value)}
            >
              <MenuItem value="all">All Severities</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={filterEntity}
              label="Entity"
              onChange={(e) => setFilterEntity(e.target.value)}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity} value={entity}>{entity}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Severity</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.map((log) => {
              const attrs = log.attributes || log;
              return (
                <TableRow key={log.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {attrs.severity && getSeverityIcon(attrs.severity)}
                      <Chip 
                        label={attrs.severity || 'info'} 
                        color={getSeverityColor(attrs.severity || 'info')} 
                        size="small" 
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {attrs.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={attrs.entity} size="small" variant="outlined" />
                    {attrs.entityId && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ID: {attrs.entityId}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {attrs.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {attrs.user?.data?.attributes?.username || attrs.user?.data?.attributes?.email || 'System'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {attrs.createdAt ? new Date(attrs.createdAt).toLocaleString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleViewDetails(log)} size="small">
                      <Visibility />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredLogs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                    No logs found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailsOpen} onClose={handleDetailsClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {viewingLog && getSeverityIcon((viewingLog.attributes || viewingLog).severity || 'info')}
            Log Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewingLog && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {(() => {
                const attrs = viewingLog.attributes || viewingLog;
                return (
                  <>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Severity</Typography>
                      <Chip label={attrs.severity || 'info'} color={getSeverityColor(attrs.severity || 'info')} size="small" />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Action</Typography>
                        <Typography variant="body1">{attrs.action}</Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Entity</Typography>
                        <Typography variant="body1">
                          {attrs.entity} {attrs.entityId && `(ID: ${attrs.entityId})`}
                        </Typography>
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      <Typography variant="body1">{attrs.description}</Typography>
                    </Box>
                    {attrs.changes && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Changes</Typography>
                        <Paper sx={{ p: 2, backgroundColor: '#f5f5f5' }}>
                          <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                            {JSON.stringify(attrs.changes, null, 2)}
                          </pre>
                        </Paper>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">User</Typography>
                        <Typography variant="body1">
                          {attrs.user?.data?.attributes?.username || attrs.user?.data?.attributes?.email || 'System'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Time</Typography>
                        <Typography variant="body1">
                          {attrs.createdAt ? new Date(attrs.createdAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                    {attrs.ipAddress && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">IP Address</Typography>
                        <Typography variant="body1">{attrs.ipAddress}</Typography>
                      </Box>
                    )}
                    {attrs.userAgent && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">User Agent</Typography>
                        <Typography variant="body2" sx={{ fontSize: '11px' }}>
                          {attrs.userAgent}
                        </Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailsClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SystemLogs;

